// SC-2768 — the Attributes view's column sort + its URL round-trip. Pure + exported
// so the agent bridge (an `open_view` directive → URL) and every comparator are
// unit-testable without a store or a router.
//
// Sorting is CLIENT-side: `useCatalogAttributes` returns every field in one page (no
// server pagination), so there's no truncation to worry about — unlike the entities
// grid, whose sort is a server cursor. We reuse the entities table's sort PRIMITIVES
// (`cycleSort`/`sortDirFor`/`ariaSort`, tableSort.ts) so the two data tables behave
// identically at the header: the same 3-state click cycle (unsorted → asc → desc →
// unsorted), the same `aria-sort`, the same arrow. Only the comparators + the URL
// home differ (attributes live in the URL for the agent seam; entities in a store).
import type { AttributeView, CanvasDirective, Sort, SortDir } from '../../../types';
import { findingsForField, type AttributeFindings } from './attributeFindings';

// The sortable columns — deliberately the THREE the agent `open_view` can address
// ({ view:'attributes', sort:'fill'|'name'|'findings' }, canvas.ts). The key IS the
// wire vocab, so the directive → URL map is 1:1 with no translation; the other
// columns (type/section/distinct/sample) stay static.
export const ATTRIBUTE_SORT_KEYS = ['name', 'fill', 'findings'] as const;
export type AttributeSortKey = (typeof ATTRIBUTE_SORT_KEYS)[number];

// The direction the AGENT's bare `sort` lands on — the "worst/most-useful first"
// reading of its intent ("which attributes are worst?" → emptiest first). A USER
// clicking a header still cycles freely from asc (via `cycleSort`); this only
// decides where the agent's directionless `sort:'fill'` points.
const AGENT_SORT_DIR: Record<AttributeSortKey, SortDir> = {
  name: 'asc', // A → Z
  fill: 'asc', // lowest fill rate first — the gaps
  findings: 'desc', // most findings first — the noise
};

export function isAttributeSortKey(x: unknown): x is AttributeSortKey {
  return typeof x === 'string' && (ATTRIBUTE_SORT_KEYS as readonly string[]).includes(x);
}

/** Compare two fields on the given axis (ascending). `findings` counts a field's
 *  field-scoped findings (fix + review) via the same index the rows render. */
function compareOn(key: AttributeSortKey, a: AttributeView, b: AttributeView, findings: AttributeFindings): number {
  if (key === 'name') return a.field.localeCompare(b.field);
  if (key === 'fill') return a.fill_rate - b.fill_rate;
  return findingsForField(findings, a.field).length - findingsForField(findings, b.field).length;
}

/** Order the fields for display. `null` (or an unknown key from a hand-edited URL)
 *  keeps the catalog's natural order. Every order breaks ties on the field name so
 *  equal keys stay deterministic — and the tiebreak is direction-independent (it
 *  doesn't flip with `desc`), so a `desc` sort's equal-key groups read A→Z too. */
export function sortAttributes(
  fields: readonly AttributeView[],
  findings: AttributeFindings,
  sort: Sort | null,
): AttributeView[] {
  if (!sort || !isAttributeSortKey(sort.key)) return [...fields];
  const key = sort.key;
  const factor = sort.dir === 'desc' ? -1 : 1;
  return [...fields].sort((a, b) => {
    const primary = compareOn(key, a, b, findings) * factor;
    return primary !== 0 ? primary : a.field.localeCompare(b.field);
  });
}

// --- URL round-trip ---------------------------------------------------------

/** Read the sort out of the URL; the inverse of {@link writeAttributeSort}. Tolerant
 *  of stale/hand-edited links: an unknown `sort` key → no sort (natural order); a
 *  missing/invalid `dir` → the parse default `'asc'`. */
export function parseAttributeSort(sp: URLSearchParams): Sort | null {
  const key = sp.get('sort');
  if (!isAttributeSortKey(key)) return null;
  return { key, dir: sp.get('dir') === 'desc' ? 'desc' : 'asc' };
}

/** Write the sort onto a copy of `prev`, preserving unrelated keys. `null` (natural
 *  order) clears both params; `'asc'` is the parse default so it's omitted for a clean
 *  URL — only `'desc'` writes `?dir=desc`. */
export function writeAttributeSort(prev: URLSearchParams, sort: Sort | null): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (!sort || !isAttributeSortKey(sort.key)) {
    next.delete('sort');
    next.delete('dir');
    return next;
  }
  next.set('sort', sort.key);
  if (sort.dir === 'desc') next.set('dir', 'desc');
  else next.delete('dir');
  return next;
}

/** Translate an agent `open_view` attributes directive (SC-2768) into the Attributes-view
 *  URL query — the same bridge findings uses (findingsDirectiveToSearch). The bare
 *  `sort` gets its natural direction ({@link AGENT_SORT_DIR}); an absent `sort` (or a
 *  bare attributes nav) yields the empty query (natural order).
 *
 *  An `attribute` overlay rides as `?field=<name>` — the param the view reads to open
 *  its field drawer on arrival (SC-2872: board drilldowns share this path; the
 *  findings view's `?finding=` is the same bridge). Other overlay kinds don't
 *  address the Attributes view and are ignored here. */
export function attributesDirectiveToSearch(directive: CanvasDirective): string {
  const p = directive.params;
  let sp = new URLSearchParams();
  if (p && p.view === 'attributes' && p.sort) {
    const sort: Sort = { key: p.sort, dir: AGENT_SORT_DIR[p.sort] };
    sp = writeAttributeSort(sp, sort);
  }
  if (directive.overlay?.kind === 'attribute') sp.set('field', directive.overlay.field);
  return sp.toString();
}
